import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { combineLatest, of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { MultiMedia, ScreenShareRtdb, Video } from '@models';
import { LayoutType } from '@enums';
import { JanusService } from '@shared/services/janus.service';
import { StateService } from '@shared/services/state.service';
import { VideoComponent } from '@shared/components/video/video.component';
import { BLOCK_BOT_NAME } from '../../constants/bot-jibri.constant';
import { RealtimeDatabaseService } from '../../services/rtdb.service';
import { CustomizationModel } from '../../models/customization.model';
import { CustomizationService } from '../../services/customization.service';
import { PlatformDetectorService } from '@shared/services/platform-detector/platform-detector.service';
import { RealtimeDatabaseSubscriptionsService } from '../../services/rtdb-subscriptions.service';
import { ZWIFT_POSTFIX } from '../../constants/zwift.constant';

@Component({
  selector: 'stusan-videos',
  templateUrl: './videos.component.html',
  styleUrls: [
    './videos.component.scss',
    './layouts/tile.scss',
    './layouts/leader.scss',
    './layouts/share.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class VideosComponent implements OnInit, AfterViewInit, OnDestroy {
  destroyed$ = new Subject<boolean>();
  public customConfig: CustomizationModel;
  private roomName: string;
  public videosArray: MultiMedia[];
  public currentArray: MultiMedia[];
  public videosArrayLength = 0;
  // firstRun = true;

  @Input() set videos(videos: MultiMedia[] | null) {
    // TODO do not add Jibri in janus.server.ts instead of this filtering
    this.videosArray = this.filterVideos(videos || []).map(item => {
      item.visible = true;
      return item;
    });
    const zwiftArray = this.getZwiftScreensVideos(videos || []);

    this.videosArray = this.unionVideosWithZwiftScreens(this.videosArray, zwiftArray);

    this.setStartEndIndexes();
    this.videosArrayLength = this.videosArray.length;
    this.pageLimit = Math.ceil(this.videosArrayLength / this.pageSize);
    this.onSetVideos(this.videosArray);

    if (this.videosArray.length >= 8) {
      this.filterVisibleVideos();
    }
  }

  get videos(): MultiMedia[] {
    return this.videosArray;
  }

  @ViewChildren('videoContainer') videoContainers: QueryList<ElementRef>;
  @ViewChildren('other') otherVideos: QueryList<VideoComponent>;
  @HostBinding('class') layoutType: LayoutType = LayoutType.leader;
  @ViewChild('sourceWrapper', { static: false, read: CdkScrollable }) sourceWrapper: CdkScrollable;
  @HostBinding('class.is-participants-shown') isParticipantsShown: boolean;
  @HostBinding('class.is-chat-shown') isChatShown: boolean;

  public videosOffset = 0;
  public offsetArray: any[] = [];
  public readonly videoSelfie = 'data-selfie';
  public readonly videoMain = 'data-main';
  public readonly videoPrevious = 'data-previous';
  public readonly videoNext = 'data-next';
  public leader: MultiMedia | any;
  public myVideo: MultiMedia | any;
  public ownVideoId = 0;

  public othersArray: MultiMedia[];
  public othersArrayLength = 0;

  needScroll = true; // false was before
  isScrollLeft = true;
  isScrollRight = true;
  manual: boolean | undefined = false;

  startIndex = 0;
  endIndex = 2;
  pageNumber = 0;
  pageSize = 2;
  currentPage = 0;
  pageLimit = 0;
  tileItemsOnScreen = 9;
  private userId: string | null;
  public isSticky: boolean;
  public isMobilePlatform = false;
  public isOthersVisible = true;
  public isMobileViewport = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updateMobileViewFromSize();
  }

  constructor(
    public janusService: JanusService,
    public stateService: StateService,
    public customizationService: CustomizationService,
    private readonly rtdb: RealtimeDatabaseService,
    private readonly platformDetectorService: PlatformDetectorService,
    private readonly rtdbSubscriptionService: RealtimeDatabaseSubscriptionsService
  ) {
    this.customConfig = customizationService.config;
    // this.firstRun = true;
    // this.test(7); // For testing /////////////////////
  }

  async ngOnInit(): Promise<void> {
    this.isMobilePlatform = this.platformDetectorService.isMobile();
    if (this.customConfig.isLikesEnabled) {
      this.stateService.layoutType = LayoutType.leader;
      this.layoutType = this.stateService.layoutType;
    } else {
      if (this.isMobilePlatform || this.isMobileViewport) {
        this.stateService.layoutType = LayoutType.leader;
        this.layoutType = this.stateService.layoutType;
      } else {
        this.stateService.layoutType = LayoutType.tile;
        this.layoutType = this.stateService.layoutType;
      }
    }
    this.ownVideoId = this.janusService.getLocalUserId();
    this.userId = this.stateService.userId;
    this.setRoomName();
    this.stateService.layoutType$.pipe(takeUntil(this.destroyed$)).subscribe((layoutType: LayoutType) => {
      this.layoutType = layoutType;
      this.setStartEndIndexes();

      if ((this.layoutType === LayoutType.leader) || (this.layoutType === LayoutType.share)) {
        this.onSetVideos(this.videosArray);
      }
      if (this.layoutType === LayoutType.leader || this.layoutType === LayoutType.share) {
        this.othersArray = this.othersArray.map((item) => {
          item.visible = true;
          return item;
        });
        this.filterOthersFromLeader();
      } else {
        this.filterVisibleVideos();
      }
    });

    this.handleUsersScreenSharesStates();

    if (this.customConfig.isLikesEnabled) {
      this.stateService.currentVideoId$.asObservable()
        .pipe(
          takeUntil(this.destroyed$),
        ).subscribe((starredVideo) => {
          this.janusService.videos$.asObservable().pipe(
            takeUntil(this.destroyed$)
          ).subscribe((videos: MultiMedia[]) => {
            this.switchToLeader(videos, starredVideo);
          });
        });
    }

    this.janusService.videos$.asObservable().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((videos: MultiMedia[]) => {
      // update stale video in leader
      if (this.leader && this.layoutType === LayoutType.leader) {
        const recentVideo = videos.find(m => m.video.id === this.leader.video.id);
        if (recentVideo && this.leader !== recentVideo) {
          console.log('Updated leader tile with fresh data.');
          this.leader = recentVideo;
        }
      }

      const ownVideo = videos.find(({ video }) => video.id === this.ownVideoId) as any;
      const currentVideos = videos.filter(({ video }) => video.id !== this.ownVideoId) as any;
      if (videos.length >= 5 && ownVideo?.video) {
        // this.stateService.isOwnVideoActive = ownVideo?.video.id;
        if (this.customConfig.isLikesEnabled) {
          this.setLeader(currentVideos[Math.floor(Math.random() * currentVideos.length)].video);
        }
      } else {
        this.stateService.isOwnVideoActive = null;
      }
    });

    this.stateService.isMobileMenuHidden$.asObservable().pipe(
      takeUntil(this.destroyed$),
    ).subscribe(isMobileMenuHidden => {
      this.isSticky = isMobileMenuHidden;
    });

    this.stateService.isChatShown$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isChatShown: boolean) => {
      this.isChatShown = isChatShown;
    });

    this.stateService.isParticipantsShown$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isParticipantsShown: boolean) => {
      this.isParticipantsShown = isParticipantsShown;
    });

    this.stateService.raisedToTopVideoId$.asObservable()
      .subscribe((raisedToTopVideoId: number | null | undefined) => {
        if (this.layoutType === LayoutType.leader) {
          const topVideo = this.videosArray.find(item => item.video.id === raisedToTopVideoId);
          if (topVideo) {
            this.setLeader(topVideo.video);
          }
        }
        this.filterVisibleVideos(raisedToTopVideoId);
      });
  }

  private switchToScreenShareView(videos: MultiMedia[], screenShareId: number | null): void {
    if (this.hasActiveScreenshareZwift(videos)) {
      return;
    }

    const ownVideo = videos.find(({ video }: MultiMedia) => video.id === this.ownVideoId) as any;
    const screenVideo = videos.find(({ video }: MultiMedia) => video.id === screenShareId);

    if (!screenVideo?.video) {
      // if (!this.firstRun) {
      //   this.setLeader(ownVideo.video);
      // }

      this.stateService.isScreenShareActive$.next(null);
      // this.stateService.isScreenShareEnabled = false;

      if (this.customConfig.leader) {
        this.stateService.layoutType = LayoutType.leader;
        this.layoutType = this.stateService.layoutType;
      } else {
        if (this.isMobilePlatform || this.isMobileViewport) {
          return;
        }

        this.stateService.layoutType = LayoutType.tile;
        this.layoutType = this.stateService.layoutType;
      }
      return;
    }

    this.stateService.isOwnVideoActive = null;
    this.stateService.isScreenShareActive$.next(screenVideo?.video.id);

    this.stateService.isLayoutChanged$.next(false);

    this.stateService.layoutType = this.customConfig.isLikesEnabled ? LayoutType.leader : LayoutType.share;
    this.layoutType = this.stateService.layoutType;

    if (!this.manual) {
      this.setLeader(screenVideo.video);
    }
  }

  public trackByVideoFn(index: number, item: MultiMedia): number {
    return item.video.id;
  }

  public trackByAudioFn(index: number, item: MultiMedia): number {
    return item.video.id;
  }

  public hasAudioAndNotLocal(source: MultiMedia) {
    return source.audio?.stream && source.video.remote;
  }

  private switchToLeader(videos: MultiMedia[], videoId: any): void {
    if (this.hasActiveScreenshareZwift(videos)) {
      return;
    }

    const leaderVideo = videos.find(({ video }) => video.id === parseInt(videoId, 10)) as any;
    if (!leaderVideo?.video) {
      return;
    }
    this.setLeader(leaderVideo.video);
  }

  private setRoomName(): void {
    this.roomName = this.stateService.roomName as string;
  }

  setStartEndIndexes() {
    if ((this.layoutType === LayoutType.leader) || (this.layoutType === LayoutType.share)) {
      if (this.leader) {
        // StanR: this is part of simulcast
        // we don't need it for now
        // this.leader.video?.pluginHandle.send({ message: { request: 'configure', video: true, substream: 2 } });
      }
      this.startIndex = 0;
      if (this.customConfig.isLikesEnabled) {
        this.endIndex = 2;
      } else {
        if (this.isMobilePlatform || this.isMobileViewport) {
          this.endIndex = 100;
          return;
        } else {
          this.endIndex = 2;
        }
      }
    } else { // Tile
      // this.endIndex = this.tileItemsOnScreen - 1; // the limit is 6 users for Tile Layout
      // for all visible videos must send video: true

      // StanR: this is part of simulcast
      // we don't need it for now
      // this.videosArray.forEach((currentVideo: MultiMedia) => {
      //   currentVideo.video?.pluginHandle.send({ message: { request: 'configure', video: true, substream: 0 } });
      // });
    }
  }

  onPrevTile() {
    if (0 <= this.currentPage - 1) {
      this.currentPage--;
      this.startIndex = this.currentPage * this.pageSize;
    }
  }

  onNextTile() {
    if (this.currentPage + 1 < this.pageLimit) { // can be wrong
      this.currentPage++;
      this.startIndex = this.currentPage * this.pageSize;
    }
  }

  showPrev(event: Event) {
    event.stopPropagation();
    if (0 <= this.startIndex - 1) {
      this.startIndex--;
      this.endIndex--;
    }
  }

  showNext(event: Event) {
    event.stopPropagation();
    if (this.othersArrayLength > this.endIndex + 1) {
      this.startIndex++;
      this.endIndex++;
    }
  }

  private onSetVideos(videos: MultiMedia[]) {
    this.filterOthersFromLeader();
    if (this.stateService.isScreenShareActive && this.leader) {
      this.leader = videos.find(item => item.video.id === this.leader.video.id);
      return;
    }
    if ((this.layoutType === LayoutType.leader) || (this.layoutType === LayoutType.share)) {
      // this.leader = videos[0];
      const firstVideo = videos[0];
      if (firstVideo) {
        this.setLeader(firstVideo.video);
      }
    }
  }

  private filterOthersFromLeader(showMore?: boolean, pageNumber?: number, pageSize?: number) {
    this.othersArray = this.videosArray
      .filter(({ video }) => this.leader && video?.id !== this.leader.video.id)
      .sort((a: any, b: any) => a.video.name.localeCompare(a.video.name))
      .sort((a, b) => Number(a.video?.remote) - Number(a.video.remote));
    if (this.customConfig.isLikesEnabled) {
      this.othersArray = this.othersArray
        .filter(({ video }) => (video?.id !== this.stateService.isOwnVideoActive$.getValue()));
      this.myVideo = this.videosArray
        .find(({ video }) => (video?.id === this.stateService.isOwnVideoActive$.getValue()));
    }
    if (this.layoutType === LayoutType.leader && !this.isMobilePlatform && !this.isMobileViewport && !this.customConfig.isLikesEnabled) {
      this.othersArray = this.othersArray.map((item, index) => {
        item.visible = index >= 0 && index <= 2;
        return item;
      });
    }

    // StanR: this is part of simulcast
    // we don't need it for now
    // this.othersArray.forEach(({ video }: MultiMedia) => {
    //   video?.pluginHandle.send({ message: { request: 'configure', video: true, substream: 0 } });
    // });
    this.othersArrayLength = this.othersArray.length;
    if (this.isMobilePlatform || this.isMobileViewport) {
      if (pageNumber && pageSize) {
        this.othersArray = this.paginateArray(pageNumber, pageSize);
        if (!this.othersArray.length) {
          this.isOthersVisible = false;
          this.seeLess();

        }
        // console.log(this.othersArray);
      } else {
        if (this.othersArray.length <= 3) {
          this.othersArray = this.paginateArray(1, 3);
        } else {
          this.othersArray = this.paginateArray(1, 2);
        }
        // console.log(this.othersArray);
      }
    }
  }

  paginateArray(pageNumber: number, pageSize: number) {
    // console.log(pageNumber);
    // console.log(pageSize);
    return this.othersArray.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  }

  setLeader(video: Video, manual?: boolean, event?: any) {
    if (event) {
      event.stopPropagation();
    }

    this.manual = manual;

    // StanR: this is part of simulcast
    // we don't need it for now
    // video.pluginHandle.send({ message: { request: 'configure', video: true, substream: 2 } });

    this.leader = { video: { ...video } };

    this.filterOthersFromLeader();

    if (!this.customConfig.isLikesEnabled) {
      this.stateService.isOwnVideoActive = null;
    }

    console.log('Leader updated ' + this.leader.video.id);
  }

  filterVisibleVideos(raisedToTopVideoId?: any) {
    if (this.layoutType === LayoutType.tile) {
      this.videosArray = this.videosArray
        .sort((a, b) => Number(this.isCamMuted(a)) - Number(this.isCamMuted(b)))
        .sort((a, b) => a.video.id === raisedToTopVideoId ? -1 : b.video.id === raisedToTopVideoId ? 1 : 0)
        .sort((a, b) => Number(a.video.remote) - Number(b.video.remote))
        .map((item, index) => {
          if (this.videosArray.length > this.tileItemsOnScreen) {
            item.visible = index >= 0 && index <= (this.tileItemsOnScreen - 2);
          } else {
            item.visible = index >= 0 && index <= (this.tileItemsOnScreen - 1);
          }
          return item;
        });
    }
  }

  isCamMuted(tile: MultiMedia): boolean {
    // TODO RTDB fix case for screen
    // if (undefined === this.janusService.camStates[id] && type === 'screen') {
    //   return false;
    // } else {
    //   return !this.janusService.camStates[id];
    // }
    if (tile.video.type === 'screen') {
      return false;
    }
    const userInfo = this.rtdbSubscriptionService.getUserInfo(tile.video.id, !tile.video.remote);
    return !userInfo.video;
  }

  pluralize(count: number, noun: string, suffix = 's') {
    return `${count} ${noun}${count !== 1 ? suffix : ''}`;
  }

  othersChanged(event: MutationRecord[]) {
    // this.setScrollStatus();
  }

  test(quantity = 6) {
    setTimeout(async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      const videos: any[] = [];
      for (let i = 0; i < quantity; i++) {
        videos.push({
          stream,
          pluginHandle: null,
          id: Math.floor(1000 + Math.random() * 9000),
          name: Math.floor(1000 + Math.random() * 9000),
          remote: i !== 0,
          type: 'video',
        });
      }
      this.janusService.videos$.next(videos);
      // setTimeout(() => {
      //   console.log('[test] ', this.sourceWrapper);
      // })
    }, 3000);
  }

  othersClasses(tiles: number): string {
    // othersClasses(videosArrayLength) for class="viewport"
    if (tiles <= 4) {
      return 'lt-four';
      // } else if (tiles === 4) {
      // return 'four';
    } else {
      return 'gt-four';
    }
  }

  ngAfterViewInit(): void {
    // this.firstRun = false;
    // this.setScrollStatus();
  }

  public id(index: number, item: Video): number {
    return item.id;
  }

  public seeMore(event: any) {
    event.stopPropagation();
    this.isOthersVisible = true;
    this.pageNumber++;
    this.pageSize = 11;
    this.filterOthersFromLeader(this.isOthersVisible, this.pageNumber, this.pageSize);
  }

  public seeLess() {
    this.isOthersVisible = false;
    this.pageNumber = 0;
    this.pageSize = 2;
    this.filterOthersFromLeader(this.isOthersVisible, this.pageNumber, this.pageSize);
  }

  noVideo(stream: MediaStream) {
    return stream.getVideoTracks().length === 0;
  }

  noAudio(stream: MediaStream) {
    return stream.getAudioTracks().length === 0;
  }

  onVideoClick(element: HTMLDivElement): void {
    if (this.layoutType === LayoutType.leader) {
      if (element.hasAttribute(this.videoPrevious)) {
        this.videosOffset--;
      } else if (element.hasAttribute(this.videoNext)) {
        this.videosOffset++;
      } else {
        // Reset previous main video
        this.videoContainers.forEach((video: ElementRef) => {
          video.nativeElement.removeAttribute(this.videoMain);
        });

        // Set current video as main
        element.setAttribute(this.videoMain, 'true');
      }

    }
  }

  scrollSource(next: boolean) {
    const distance = Math.ceil(this.sourceWrapper.getElementRef().nativeElement.getBoundingClientRect().width);
    const to = next ? { start: this.sourceWrapper.measureScrollOffset('start') + distance } : {
      end: this.sourceWrapper.measureScrollOffset('end') + distance
    };
    this.sourceWrapper.scrollTo({
      ...to,
      behavior: 'smooth',
    });
    // this.setScrollStatus();
  }

  // public setScrollStatus() {
  //   if (this.sourceWrapper) {
  //     const el = this.sourceWrapper.getElementRef().nativeElement;
  //     this.needScroll = el.offsetWidth < el.scrollWidth;
  //     this.isScrollLeft = this.sourceWrapper.measureScrollOffset('start') > 0;
  //     this.isScrollRight = this.sourceWrapper.measureScrollOffset('end') > 0;
  //   }
  // }

  private handleUsersScreenSharesStates(): void {
    this.rtdbSubscriptionService.screenShareSubscription$
      .pipe(
        switchMap((screenSharesData: { [userId: string]: ScreenShareRtdb } | null) =>
          combineLatest([this.janusService.videos$.asObservable(), of(screenSharesData)])
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(([videos, screenSharesData]: [MultiMedia[], { [userId: string]: ScreenShareRtdb } | null]) => {
        if (this.hasActiveScreenshareZwift(videos)) {
          return;
        }

        const lastScreenShareId = this.getTheLatestExistingScreenShareId(screenSharesData, videos);
        const hasActualScreenShares = screenSharesData && Object.values(screenSharesData);
        // console.log('Screenshare changed ' + screenShareId + ',' +
        //   ' layout changed: ' + this.stateService.isLayoutChanged);
        this.updateMobileViewFromSize();

        if (!lastScreenShareId && !hasActualScreenShares) {
          // if (!this.isMobilePlatform && !this.isMobileViewport && !this.customConfig.isLikesEnabled) {
          //   this.stateService.layoutType = LayoutType.tile;
          //   this.layoutType = this.stateService.layoutType;
          // }
          return;
        }

        this.switchToScreenShareView(videos, lastScreenShareId);
      });
  }

  private filterVideos(videos: MultiMedia[]): MultiMedia[] {
    // TODO: remove this logic when we will use something else instead of deleting bot via his name
    return videos.filter(({ video }: MultiMedia) => video.name !== BLOCK_BOT_NAME && !this.isZwiftScreen(video)) || [];
  }

  private getZwiftScreensVideos(videos: MultiMedia[]): MultiMedia[] {
    return videos.filter(({ video }: MultiMedia) => this.isZwiftScreen(video)) || [];
  }

  private unionVideosWithZwiftScreens(multiMedias: MultiMedia[], zwiftScreens: MultiMedia[]): MultiMedia[] {
    return multiMedias.map((multiMedia: MultiMedia) => {
      const zwiftScreen = zwiftScreens.find(({ video }: MultiMedia) => multiMedia?.video.name + ZWIFT_POSTFIX === video.name);

      return zwiftScreen ? { ...multiMedia, zwiftScreen: zwiftScreen.video } : multiMedia;
    });
  }

  private hasActiveScreenshareZwift(videos: MultiMedia[]): boolean {
    return videos?.some(({ video }: MultiMedia) => this.isZwiftScreen(video));
  }

  private isZwiftScreen(video: Video): boolean {
    return video.name.endsWith(ZWIFT_POSTFIX);
  }

  private getTheLatestExistingScreenShareId(
    screenShareData: { [userId: string]: ScreenShareRtdb } | null,
    videos: MultiMedia[]
  ): number | null {
    if (!screenShareData) {
      return null;
    }

    const actualScreenShares = Object.values(screenShareData)
      .filter((value: ScreenShareRtdb) => !!videos.find((video: MultiMedia) => value.screenShareId === video.video.id));

    if (!actualScreenShares?.length) {
      return null;
    }

    const lastScreenShare = actualScreenShares
      .reduce(
        (biggestScreenShare: ScreenShareRtdb, currentScreenShare: ScreenShareRtdb) =>
          (biggestScreenShare.timestamp > currentScreenShare.timestamp) ? biggestScreenShare : currentScreenShare
      );

    return lastScreenShare.screenShareId;
  }

  private updateMobileViewFromSize() {
    const isTestRoom = this.stateService.roomName === 'stanr';
    this.isMobileViewport = window.innerWidth < (isTestRoom ? 792 : 992);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
